import angular from 'angular'

import { wrap, uniqueId } from 'lodash-es'

import envRaw from '../utils/env-raw'
import postPosition from '../utils/post-position'

import NaiveHeap from '../utils/naive-heap'
import NaiveQueue from '../utils/naive-queue'

import Template from './gallery-preview-flow.component.ejs'

angular
  .module('ggs.gallery-preview-flow')
  .component('ggsGalleryPreviewFlow', {
    template: Template({
      LE: envRaw.liveEditMode
    }),
    controller: GalleryPreviewFlowComponent,
    bindings: {
      ggsStylerNew: '<',
      ggsProperties: '<',
      ggsPostList: '<',
      ggsPostVoteEmitter: '<',
      ggsFlowAttachCallback: '<',
      ggsFlowInViewportHandler: '<',
      ggsCanShowPreview: '<',
      ggsPreviewDoneHandler: '<',
      ggsPreviewFailHandler: '<',
      ggsPreviewDenyHandler: '<',
      ggsPreviewSelectHandler: '<'
    }
  })

const ISO_GRID_CONTENT_QS = '.ggs-gallery-preview-flow__isotope-grid-content'
const ISO_GRID_SHOWCASE_QS = '.ggs-gallery-preview-flow__isotope-grid-showcase'
const ISO_GRID_SHOWCASE_CUT_QS = '.ggs-gallery-preview-flow__isotope-grid-showcase-cut'

const CAROUSEL_NEXT_QS = '.ggs-gallery-preview-flow__carousel-button_next'
const CAROUSEL_PREV_QS = '.ggs-gallery-preview-flow__carousel-button_prev'

const PREVIEW_HOVER_QS = '.ggs-gallery-preview__showcase:hover'

function GalleryPreviewFlowComponent(
  qh,
  $log,
  domh,
  $scope,
  $element,
  ForceHover,
  FlowIsotope,
  DefineMethod,
  DefineHandler,
  PostSpaceStyleHandler,
  GalleryPreviewFlowStyleHandlers) {

  const sign = `galleryFlow[uid=${uniqueId()}]`
  const $ctrl = $scope.$ctrl = this

  $ctrl.wrapH = DefineHandler(sign)
  $ctrl.wrapM = DefineMethod(sign)

  $ctrl.isoGridContentElm = domh.find($element, ISO_GRID_CONTENT_QS)
  $ctrl.isoGridShowcaseElm = domh.find($element, ISO_GRID_SHOWCASE_QS)
  $ctrl.isoGridShowcaseCutElm = domh.find($element, ISO_GRID_SHOWCASE_CUT_QS)

  $ctrl.carouselNextElm = domh.find($element, CAROUSEL_NEXT_QS)
  $ctrl.carouselPrevElm = domh.find($element, CAROUSEL_PREV_QS)

  // components
  $ctrl.previewsForceHover = ForceHover(PREVIEW_HOVER_QS)

  $ctrl.flowIsotope = FlowIsotope(
    $ctrl.isoGridContentElm,
    $ctrl.isoGridShowcaseElm,
    $ctrl.carouselNextElm,
    $ctrl.carouselPrevElm,
    $ctrl.ggsProperties,
    $ctrl.ggsCanShowPreview,
    $ctrl.ggsFlowInViewportHandler)

  // <ggs-gallery/> export
  const extFlow = ({ wrapM: DefineMethod('ExtFlow') })

  extFlow
    .wrapM('flushPrependQueue', $ctrl.flowIsotope.flushPrependQueue)
    .wrapM('flushAppendQueue', $ctrl.flowIsotope.flushAppendQueue)
    .wrapM('showPreviewsDesc', $ctrl.previewsForceHover.apply)
    .wrapM('hidePreviewsDesc', $ctrl.previewsForceHover.clear)
    .wrapM('instantMode', $ctrl.flowIsotope.instantMode)
    .wrapM('lazyMode', $ctrl.flowIsotope.lazyMode)
    .wrapM('scrollTo', $ctrl.flowIsotope.scrollTo)
    .wrapM('arrange', $ctrl.flowIsotope.arrange)
    .wrapM('resize', $ctrl.flowIsotope.resize)
    .wrapM('remove', $ctrl.flowIsotope.remove)

  // styler stuff
  const postSpaceHandler = PostSpaceStyleHandler(extFlow.resize)
  const styleHandlers = GalleryPreviewFlowStyleHandlers($ctrl.flowIsotope.resize)

  // initHandler
  $ctrl.wrapM('attachToParent', () => {
    return $ctrl.ggsFlowAttachCallback(
      extFlow.flushPrependQueue,
      extFlow.flushAppendQueue,
      extFlow.showPreviewsDesc,
      extFlow.hidePreviewsDesc,
      extFlow.instantMode,
      extFlow.lazyMode,
      extFlow.scrollTo,
      extFlow.arrange,
      extFlow.resize,
      extFlow.remove)
  })

  $ctrl.wrapM('attachToStyler',
    qh.makeCatch(
      qh.makeOver(
        wrap(styleHandlers, $ctrl.ggsStylerNew.reg),
        wrap(postSpaceHandler, $ctrl.ggsStylerNew.reg)),
      wrap(`${sign}#attachToStyler: error halted:`, $log.error)))

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.attachToParent,
      $ctrl.attachToStyler))

  // destroyHandler
  $ctrl.wrapH('$onDestroy',
    qh.makeJoin(
      $ctrl.flowIsotope.destroy,
      $ctrl.previewsForceHover.destroy,
      wrap(styleHandlers, $ctrl.ggsStylerNew.release),
      wrap(postSpaceHandler, $ctrl.ggsStylerNew.release)))

  // <ggs-preview/> handlers
  $ctrl.prependOrderKeeper = $ctrl.ggsProperties.strict_order ? NaiveQueue() : NaiveHeap()
  $ctrl.appendOrderKeeper = $ctrl.ggsProperties.strict_order ? NaiveQueue() : NaiveHeap()

  $ctrl.tryPrepend = () => {
    while ($ctrl.prependOrderKeeper.isNextReady()) {
      const reg = $ctrl.prependOrderKeeper.next()
      $ctrl.flowIsotope.prepend(reg)
    }
  }

  $ctrl.tryAppend = () => {
    while ($ctrl.appendOrderKeeper.isNextReady()) {
      const reg = $ctrl.appendOrderKeeper.next()
      $ctrl.flowIsotope.append(reg)
    }
  }

  $ctrl.wrapH('previewInitHandler',
    (post) => {
      if (postPosition.isAbove(post)) {
        $ctrl.prependOrderKeeper.stay(post)
        $ctrl.tryPrepend()
      } else {
        $ctrl.appendOrderKeeper.stay(post)
        $ctrl.tryAppend()
      }
    })

  $ctrl.wrapH('previewReadyHandler',
    (post, reg) => {
      if (postPosition.isAbove(post)) {
        $ctrl.prependOrderKeeper.ready(post, reg)
        $ctrl.tryPrepend()
      } else {
        $ctrl.appendOrderKeeper.ready(post, reg)
        $ctrl.tryAppend()
      }
    })

  $ctrl.wrapH('previewFailHandler',
    (post) => {
      if (postPosition.isAbove(post)) {
        $ctrl.prependOrderKeeper.kick(post)
        $ctrl.tryPrepend()
      } else {
        $ctrl.appendOrderKeeper.kick(post)
        $ctrl.tryAppend()
      }
      return $ctrl.ggsPreviewFailHandler(post)
    })

  $ctrl.wrapH('previewDenyHandler',
    (post) => {
      if (postPosition.isAbove(post)) {
        $ctrl.prependOrderKeeper.kick(post)
        $ctrl.tryPrepend()
      } else {
        $ctrl.appendOrderKeeper.kick(post)
        $ctrl.tryAppend()
      }
      return $ctrl.ggsPreviewDenyHandler(post)
    })

  $ctrl.wrapH('previewDestroyHandler',
    (post) => {
      if (postPosition.isAbove(post)) {
        $ctrl.prependOrderKeeper.kick(post)
        $ctrl.tryPrepend()
      } else {
        $ctrl.appendOrderKeeper.kick(post)
        $ctrl.tryAppend()
      }
      return $ctrl.flowIsotope.removeVirtual(post)
    })

  $ctrl.wrapH('previewDoneHandler',
    (post) =>
      $ctrl.ggsPreviewDoneHandler(post))

  $ctrl.wrapH('previewSelectHandler',
    (post) =>
      $ctrl.ggsPreviewSelectHandler(post))
}
